// One list of setup steps (spec/13 §13.1, F-HQ-03): each with what it is for in a line, where it stands — Done, To do,
// Skipped, in words as well as by its mark — and one action that opens where it is done. A suggested step can be skipped
// (and the skip taken back) by a member who may update the venue; an essential one never.

import type { OrganizationSetupStep, VenueSetupStep } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Icon } from '../shell/nav-icons';
import type { SetupDialog, StepAction } from './steps';

type Step = OrganizationSetupStep | VenueSetupStep;

const TONE = { done: 'ok', todo: 'neutral', skipped: 'neutral' } as const;

export function SetupSteps<TStep extends Step>({
  steps,
  label,
  actionFor,
  onDialog,
  onSkip,
  busyStep,
}: {
  readonly steps: readonly TStep[];
  /** The list's accessible name: "Essentials", "Suggested", "Your business". */
  readonly label: string;
  readonly actionFor: (step: TStep) => StepAction;
  readonly onDialog: (dialog: SetupDialog) => void;
  /** Skip or take back a suggested step. Absent: the member may not, and nothing is offered. */
  readonly onSkip?: ((step: TStep, skip: boolean) => void) | undefined;
  /** The step whose skip is being saved. */
  readonly busyStep?: string | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();

  return (
    <ol className="console-setup-steps" aria-label={label}>
      {steps.map((step, index) => {
        const title = t(`setup.step.${step.id}.title`);
        const skip = 'skip' in step ? step.skip : null;
        const action = actionFor(step);
        const done = step.status === 'done';
        return (
          <li key={step.id} className="console-setup-step" data-status={step.status} data-kind={step.kind}>
            <span className="console-setup-step__mark" aria-hidden="true">
              {done ? <Icon name="check" /> : step.status === 'skipped' ? <Icon name="minus" /> : index + 1}
            </span>
            <div className="console-setup-step__text">
              <h3 className="console-setup-step__title">{title}</h3>
              <p className="console-setup-step__why">{t(`setup.step.${step.id}.why`)}</p>
              {step.status === 'skipped' && skip !== null ? (
                <p className="console-subtle console-setup-step__skipped">{t('setup.skippedOn', { date: format.date(skip.at, 'medium') })}</p>
              ) : null}
            </div>
            <div className="console-setup-step__state">
              <Badge tone={TONE[step.status]} dot>
                {t(`setup.status.${step.status}`)}
              </Badge>
              {step.kind === 'essential' && !done ? <Badge tone="accent">{t('setup.essential')}</Badge> : null}
            </div>
            <div className="console-setup-step__actions">
              {/* Done, a step keeps a quiet way back to where it was done — but not "Create a venue" once there is one. */}
              {done && action.kind === 'dialog' && action.dialog === 'createVenue' ? null : (
                <StepActionControl
                  action={action}
                  label={t(`setup.step.${step.id}.action`)}
                  variant={done ? 'ghost' : step.kind === 'essential' ? 'primary' : 'secondary'}
                  name={title}
                  onDialog={onDialog}
                />
              )}
              {onSkip !== undefined && step.kind === 'suggested' && step.status === 'todo' ? (
                <Button variant="ghost" size="sm" busy={busyStep === step.id} label={t('setup.skipNamed', { step: title })} onClick={() => onSkip(step, true)}>
                  {t('setup.skip')}
                </Button>
              ) : null}
              {onSkip !== undefined && step.status === 'skipped' ? (
                <Button variant="ghost" size="sm" busy={busyStep === step.id} label={t('setup.unskipNamed', { step: title })} onClick={() => onSkip(step, false)}>
                  {t('setup.unskip')}
                </Button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** A step's one action: a link into the screen (and row) where it is done, or a dialog opened here. */
export function StepActionControl({
  action,
  label,
  name,
  variant,
  onDialog,
}: {
  readonly action: StepAction;
  readonly label: string;
  /** The step's title, so "Choose" is announced as what it chooses. */
  readonly name: string;
  readonly variant: 'primary' | 'secondary' | 'ghost';
  readonly onDialog: (dialog: SetupDialog) => void;
}): ReactNode {
  const accessible = label === name ? label : `${label}: ${name}`;
  if (action.kind === 'dialog') {
    return (
      <Button variant={variant} size="sm" label={accessible} onClick={() => onDialog(action.dialog)}>
        {label}
      </Button>
    );
  }
  return (
    <Link
      className={`clav-button clav-button--${variant} clav-button--sm`}
      to={action.to}
      params={action.params}
      {...(action.hash === undefined ? {} : { hash: action.hash })}
      aria-label={accessible}
    >
      {label}
    </Link>
  );
}
